import { eventBus } from "#blokkli/helpers/eventBus";
import { useRuntimeConfig, computed, watch } from "#imports";
import { onlyUnique } from "./index.js";
class ConfigMap {
  configs = [];
  mapEntityType = {};
  mapEntityTypeBundle = {};
  mapEntityTypeBundleName = {};
  constructor(items) {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      this.configs.push(item);
      if (!this.mapEntityType[item.entityType]) {
        this.mapEntityType[item.entityType] = [];
      }
      this.mapEntityType[item.entityType].push(item);
      if (!this.mapEntityTypeBundle[item.entityType]) {
        this.mapEntityTypeBundle[item.entityType] = {};
      }
      if (!this.mapEntityTypeBundle[item.entityType][item.entityBundle]) {
        this.mapEntityTypeBundle[item.entityType][item.entityBundle] = [];
      }
      this.mapEntityTypeBundle[item.entityType][item.entityBundle].push(item);
      if (!this.mapEntityTypeBundleName[item.entityType]) {
        this.mapEntityTypeBundleName[item.entityType] = {};
      }
      if (!this.mapEntityTypeBundleName[item.entityType][item.entityBundle]) {
        this.mapEntityTypeBundleName[item.entityType][item.entityBundle] = {};
      }
      this.mapEntityTypeBundleName[item.entityType][item.entityBundle][item.name] = item;
    }
  }
  forEntityType(entityType) {
    return this.mapEntityType[entityType] || [];
  }
  forEntityTypeAndBundle(entityType, entityBundle) {
    return this.mapEntityTypeBundle[entityType]?.[entityBundle] || [];
  }
  forName(entityType, entityBundle, name) {
    return this.mapEntityTypeBundleName[entityType]?.[entityBundle]?.[name];
  }
  all() {
    return this.configs;
  }
}
export default async function(adapter, selection, context) {
  const bundleDefinitions = await adapter.getAllBundles();
  const itemEntityType = useRuntimeConfig().public.blokkli.itemEntityType;
  const fieldConfig = new ConfigMap(await adapter.getFieldConfig());
  const editableFieldConfigData = adapter.getEditableFieldConfig ? await adapter.getEditableFieldConfig() : [];
  const editableFieldConfig = new ConfigMap(editableFieldConfigData);
  const droppableFieldConfigData = adapter.getDroppableFieldConfig ? await adapter.getDroppableFieldConfig() : [];
  const droppableFieldConfig = new ConfigMap(droppableFieldConfigData);
  const allowedTypesInList = computed(() => {
    if (!selection.blocks.value.length) {
      return [];
    }
    let hostType = "";
    let hostBundle = "";
    let fieldName = "";
    for (let i = 0; i < selection.blocks.value.length; i++) {
      const block = selection.blocks.value[i];
      if (i !== 0 && (hostType !== block.hostType || hostBundle !== block.hostBundle || fieldName !== block.hostFieldName)) {
        return [];
      }
      hostType = block.hostType;
      hostBundle = block.hostBundle;
      fieldName = block.hostFieldName;
    }
    return fieldConfig.forName(hostType, hostBundle, fieldName)?.allowedBundles || [];
  });
  watch(selection.blocks, () => {
    if (selection.blocks.value.length !== 1) {
      return;
    }
    const item = selection.blocks.value[0];
    const hasNested = itemBundlesWithNested.includes(item.itemBundle);
    if (hasNested) {
      const nestedFields = fieldConfig.forEntityTypeAndBundle(itemEntityType, item.itemBundle).map((v) => v.name) || [];
      if (nestedFields.length === 1) {
        eventBus.emit("setActiveFieldKey", `${item.uuid}:${nestedFields[0]}`);
        return;
      }
    }
    eventBus.emit("setActiveFieldKey", `${item.hostUuid}:${item.hostFieldName}`);
  });
  const itemBundlesWithNested = fieldConfig.forEntityType(itemEntityType).map((v) => v.entityBundle) || [];
  const typeMap = bundleDefinitions.reduce((acc, type) => {
    acc[type.id] = type;
    return acc;
  }, {});
  function getBlockBundleDefinition(bundle) {
    return typeMap[bundle];
  }
  function getFieldConfig(entityType, entityBundle, fieldName) {
    return fieldConfig.forName(entityType, entityBundle, fieldName);
  }
  function getDroppableFieldConfig(fieldName, host) {
    const entityType = "entityType" in host ? host.entityType : host.type;
    const entityBundle = "itemBundle" in host ? host.itemBundle : host.bundle;
    const config = droppableFieldConfig.forName(
      entityType,
      entityBundle,
      fieldName
    );
    if (!config) {
      throw new Error(
        `Missing droppable field config for field name "${fieldName}" on entity type "${entityType}" of bundle "${entityBundle}"`
      );
    }
    return config;
  }
  const bundlesAllowedOnPage = fieldConfig.forEntityTypeAndBundle(
    context.value.entityType,
    context.value.entityBundle
  ).flatMap((v) => v.allowedBundles).filter(Boolean);
  const bundlesAllowedOnBlocks = bundlesAllowedOnPage.flatMap(
    (bundle) => fieldConfig.forEntityTypeAndBundle(itemEntityType, bundle)
  ).flatMap((v) => v.allowedBundles) || [];
  const allAllowedBundles = [
    ...bundlesAllowedOnPage,
    ...bundlesAllowedOnBlocks
  ].filter(onlyUnique);
  const generallyAvailableBundles = bundleDefinitions.filter(
    (v) => allAllowedBundles.includes(v.id)
  );
  return {
    itemBundlesWithNested,
    allowedTypesInList,
    getBlockBundleDefinition,
    getDroppableFieldConfig,
    generallyAvailableBundles,
    getFieldConfig,
    editableFieldConfig,
    droppableFieldConfig,
    fieldConfig
  };
}
